import { Capacitor } from '@capacitor/core';

const NEXT_ROUND_NOTIFICATION_ID = 49101;

class RoundNotificationManager {
  private initialized = false;
  private enabled = false;
  private localNotifications: (typeof import('@capacitor/local-notifications'))['LocalNotifications'] | null = null;

  private isNativeApp(): boolean {
    return Capacitor.isNativePlatform();
  }

  async init(): Promise<void> {
    if (!this.isNativeApp() || this.initialized) return;
    this.initialized = true;

    try {
      const mod = await import('@capacitor/local-notifications');
      this.localNotifications = mod.LocalNotifications;

      const currentPerm = await this.localNotifications.checkPermissions();
      if (currentPerm.display !== 'granted') {
        const requested = await this.localNotifications.requestPermissions();
        if (requested.display !== 'granted') {
          this.enabled = false;
          return;
        }
      }

      this.enabled = true;
    } catch (err) {
      console.warn('[RoundNotificationManager] Local notifications unavailable:', err);
      this.enabled = false;
    }
  }

  private async ensureEnabled(): Promise<boolean> {
    if (!this.initialized) await this.init();
    return this.enabled && !!this.localNotifications;
  }

  async cancelNextRoundNotification(): Promise<void> {
    if (!await this.ensureEnabled() || !this.localNotifications) return;
    try {
      await this.localNotifications.cancel({
        notifications: [{ id: NEXT_ROUND_NOTIFICATION_ID }],
      });
    } catch {
      // ignore cancellation errors
    }
  }

  async scheduleNextRoundNotification(triggerAtMs: number, body: string): Promise<void> {
    if (!await this.ensureEnabled() || !this.localNotifications) return;

    const now = Date.now();
    const at = new Date(Math.max(now + 900, triggerAtMs));

    await this.cancelNextRoundNotification();
    try {
      await this.localNotifications.schedule({
        notifications: [{
          id: NEXT_ROUND_NOTIFICATION_ID,
          title: 'PickItUp',
          body,
          schedule: { at, allowWhileIdle: true },
        }],
      });
    } catch (err) {
      console.warn('[RoundNotificationManager] Could not schedule local notification:', err);
    }
  }
}

export default new RoundNotificationManager();
